/**
 * Email validation utility functions
 */

// Basic disposable email domain list (can be extended)
export const disposableDomains = [
  "mailinator.com",
  "10minutemail.com",
  "tempmail.com",
  "guerrillamail.com",
  "yopmail.com",
  "trashmail.com"
];

/**
 * Basic email format check
 * @param {string} email - Email address to validate
 * @returns {boolean} - True if email format is valid
 */
export const isValidEmailFormat = (email) => {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return regex.test(email);
};

/**
 * Check if domain is disposable
 * @param {string} email - Email address to check
 * @returns {boolean} - True if email domain is in disposable list
 */
export const isDisposableEmail = (email) => {
  const domain = email.split("@")[1]?.toLowerCase();
  if (!domain) return false;
  return disposableDomains.includes(domain);
};

/**
 * Optional MX record check (using DNS over HTTPS - free)
 * @param {string} domain - Domain to check for MX records
 * @returns {Promise<boolean>} - True if domain has valid MX records
 */
export const checkDomainExists = async (domain) => {
  try {
    const res = await fetch(`https://dns.google/resolve?name=${domain}&type=MX`);
    const data = await res.json();
    return data.Answer && data.Answer.length > 0; // MX record found
  } catch (err) {
    console.error('Error checking domain MX records:', err);
    return false; // If DNS lookup fails, assume invalid
  }
};

/**
 * Comprehensive email validation
 * @param {string} email - Email address to validate
 * @returns {Promise<{isValid: boolean, message: string}>} - Validation result and message
 */
export const validateEmail = async (email) => {
  if (!isValidEmailFormat(email)) {
    return { isValid: false, message: "Please enter a valid email address." };
  }

  if (isDisposableEmail(email)) {
    return { isValid: false, message: "Disposable emails are not allowed." };
  }

  const domain = email.split("@")[1];
  try {
    const domainExists = await checkDomainExists(domain);
    if (!domainExists) {
      return { isValid: false, message: "This email domain seems invalid." };
    }
  } catch (error) {
    console.error('Error during domain validation:', error);
    // Continue if MX check fails - don't block submission
  }

  return { isValid: true, message: "" };
};
